import { onCLS, onINP, onLCP, onFCP, onTTFB, type Metric } from 'web-vitals'
import { Sentry } from './sentry'

/**
 * Performance monitoring using Web Vitals
 * 
 * Tracks Core Web Vitals and reports to monitoring services:
 * - CLS (Cumulative Layout Shift)
 * - INP (Interaction to Next Paint) - replaced FID in Core Web Vitals
 * - LCP (Largest Contentful Paint)
 * - FCP (First Contentful Paint)
 * - TTFB (Time to First Byte)
 */

interface PerformanceConfig {
  enabled: boolean
  reportToConsole: boolean
  reportToSentry: boolean
  reportToAnalytics: boolean
}

const defaultConfig: PerformanceConfig = {
  enabled: true,
  reportToConsole: import.meta.env.DEV,
  reportToSentry: import.meta.env.PROD,
  reportToAnalytics: import.meta.env.VITE_ANALYTICS_ENABLED === 'true',
}

/**
 * Report metric to various services
 */
function reportMetric(metric: Metric, config: PerformanceConfig): void {
  const { name, value, rating, delta } = metric

  // Console logging (development)
  if (config.reportToConsole) {
    console.info(
      `[Performance] ${name}: ${value.toFixed(2)}ms (${rating})`,
      { delta: delta.toFixed(2) }
    )
  }

  // Sentry performance monitoring
  if (config.reportToSentry && Sentry) {
    try {
      Sentry.captureMessage(`Performance: ${name}`, {
        level: 'info',
        tags: {
          metric: name,
          rating,
        },
        extra: {
          value,
          delta,
        },
      })
    } catch (error) {
      // Silently fail if Sentry is not properly configured
      console.debug('Failed to report to Sentry:', error)
    }
  }

  // Google Analytics 4
  if (config.reportToAnalytics && typeof window.gtag === 'function') {
    window.gtag('event', 'web_vitals', {
      metric_name: name,
      metric_value: Math.round(value),
      metric_rating: rating,
    })
  }

  // Plausible Analytics (custom events)
  if (config.reportToAnalytics && typeof window.plausible === 'function') {
    window.plausible('Web Vitals', {
      props: {
        metric: name,
        value: Math.round(value),
        rating,
      },
    })
  }
}

/**
 * Initialize performance monitoring
 */
export function initPerformanceMonitoring(config: Partial<PerformanceConfig> = {}): void {
  const finalConfig: PerformanceConfig = { ...defaultConfig, ...config }

  if (!finalConfig.enabled) {
    console.info('Performance monitoring disabled')
    return
  }

  // Track Core Web Vitals
  onCLS((metric) => reportMetric(metric, finalConfig))
  onINP((metric) => reportMetric(metric, finalConfig))
  onLCP((metric) => reportMetric(metric, finalConfig))
  onFCP((metric) => reportMetric(metric, finalConfig))
  onTTFB((metric) => reportMetric(metric, finalConfig))

  console.info('Performance monitoring initialized')
}

/**
 * Measure custom performance metrics
 */
export function measurePerformance(name: string, startMark?: string, endMark?: string): number | null {
  if (!('performance' in window)) {
    return null
  }

  try {
    const measure = performance.measure(name, startMark, endMark)
    console.debug(`[Performance] ${name}: ${measure.duration.toFixed(2)}ms`)
    return measure.duration
  } catch (error) {
    console.error(`Failed to measure performance for ${name}:`, error)
    return null
  }
}

/**
 * Mark a performance timestamp
 */
export function markPerformance(name: string): void {
  if ('performance' in window && 'mark' in window.performance) {
    performance.mark(name)
  }
}

// Extend Window interface for analytics globals
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void
    plausible?: (eventName: string, options?: { props?: Record<string, unknown>; url?: string }) => void
  }
}
